import "media-chrome"
import "youtube-video-element"
import { handleTranscript } from "./videoTranscript"
import { getSsObj, updateSsObject } from "../../utils/storage"
import { Emitter } from "../../utils/event-emitter"
import {
  getYouTubeVideoId,
  getYouTubeThumbnail,
  saveCurrentTime,
  togglePipMode,
  throttledTimeUpdate,
  createPipPlayer,
  VIDEO_STORAGE_PREFIX,
  CONSTANT_CLASS,
} from "../../utils/videoHelpers"
import { convertToEmbedUrl } from "../../modifications/convertLiveStreamUrl"
import { liveStreamCountdown } from "../Livestream/countdown"

// ************************************************************ //
// ******************** MAIN VIDEO PLAYER  ******************** //
// ************************************************************ //
const VideoPlayer = (videoComponentEl, baseClass) => {
  if (videoComponentEl.classList.contains("video-placeholder")) return // Skip placeholder components

  // ******* Video Player Elements *******
  const logoPlayButton = videoComponentEl.querySelector(
    `.${CONSTANT_CLASS}__logo-play-button`,
  )
  const videoWrapperEl = videoComponentEl.querySelector(`.${CONSTANT_CLASS}`)
  const videoPlayerEl = videoWrapperEl.querySelector(
    `.${CONSTANT_CLASS}__player`,
  )
  const videoPipBtnEl = videoWrapperEl.querySelector(
    `.${CONSTANT_CLASS}__pip-button`,
  )
  const videoExitPipBtn = videoWrapperEl.querySelector(
    `.${CONSTANT_CLASS}__exit-pip-btn`,
  )
  const wrapperExitPipBtnEl = videoComponentEl.querySelector(
    `.${CONSTANT_CLASS}__wrapper-exit-pip-btn`,
  )

  let videoSrc = videoPlayerEl.src || videoPlayerEl.getAttribute("src")
  let videoId = getYouTubeVideoId(videoPlayerEl.src)

  if (videoComponentEl.classList.contains("livestream")) {
    if (videoComponentEl.dataset.liveUrl) {
      videoSrc = convertToEmbedUrl(videoComponentEl.dataset.liveUrl)
      videoId = getYouTubeVideoId(videoSrc)
      videoPlayerEl.src = convertToEmbedUrl(videoComponentEl.dataset.liveUrl)
    } else {
      return console.warn("Missing livestream URL")
    }
  }

  if (!videoSrc) {
    console.error(
      "No video source found for the video player element.",
      videoPlayerEl,
    )
    return
  }

  if (!videoId) {
    console.error("Invalid YouTube video URL:", videoPlayerEl.src)
    return
  }

  const storageKey = VIDEO_STORAGE_PREFIX + videoId
  updateSsObject(storageKey, {
    videoId,
    videoSrc,
  })

  videoPlayerEl.currentTime = getSsObj(storageKey)?.currentTime || 0

  // Mobile-specific: Ensure controls show proper state on load
  if (/Mobi|Android/i.test(navigator.userAgent)) {
    // Add mobile-specific event listeners for better control state management
    videoPlayerEl.addEventListener("loadeddata", () => {
      // Force pause state sync on mobile after data loads
      if (videoPlayerEl.paused) {
        setTimeout(() => {
          videoPlayerEl.dispatchEvent(new Event("pause"))
        }, 100)
      }
    })

    // Handle mobile touch interactions with video controls
    videoPlayerEl.addEventListener("click", () => {
      // Ensure mobile controls are visible after interaction
      const videoControls = videoWrapperEl.querySelector(
        `.${CONSTANT_CLASS}__controls`,
      )
      if (videoControls) {
        videoControls.classList.add("mobile-controls-active")
        setTimeout(() => {
          videoControls.classList.remove("mobile-controls-active")
        }, 3000)
      }
    })
  }
  // Register Video Transcript functionality
  handleTranscript(videoComponentEl, videoPlayerEl, videoId, baseClass)

  function cleanUpLogoPlayButton() {
    videoComponentEl.dataset.playIconPressed = "true"
    logoPlayButton.style.display = "none"
    const videoControls = videoWrapperEl.querySelector(
      `.${CONSTANT_CLASS}__controls`,
    )
    if (videoControls) {
      videoControls.style.display = "block"
    }
  }

  // ******** Event Handlers ********
  function playVideo() {
    if (logoPlayButton) {
      cleanUpLogoPlayButton()
    }
    videoPlayerEl.play()
  }

  function handlePip() {
    togglePipMode(videoId, videoSrc, storageKey)
  }

  function handleSaveCurrentTime() {
    if (videoComponentEl.dataset.playIconPressed !== "true") {
      cleanUpLogoPlayButton()
    }
    saveCurrentTime(videoPlayerEl, videoId, storageKey)
  }

  const handleThrottledTimeUpdate = throttledTimeUpdate(
    videoPlayerEl,
    videoId,
    storageKey,
  )

  videoPlayerEl.addEventListener("loadedmetadata", handleThrottledTimeUpdate)
  videoPlayerEl.addEventListener("timeupdate", handleThrottledTimeUpdate)
  // Hide the logo/play overlay whenever playback starts (no matter how it's triggered)
  videoPlayerEl.addEventListener("play", () => {
    if (logoPlayButton) {
      cleanUpLogoPlayButton()
    }
  })
  videoPlayerEl.addEventListener("pause", handleSaveCurrentTime)
  videoPlayerEl.addEventListener("ended", handleSaveCurrentTime)
  ;[wrapperExitPipBtnEl, videoExitPipBtn, videoPipBtnEl].forEach((el) =>
    el.addEventListener("click", handlePip),
  )

  if (logoPlayButton) {
    logoPlayButton.addEventListener("click", playVideo)
  }

  return videoId
}

// ************************************************************ //
// ******************** PERSISTENT VIDEO ********************** //
// ************************************************************ //
const videoFromPip = (pageVideoIds) => {
  const storedVideoFromPip = getSsObj("pip-video", false)
  if (!storedVideoFromPip) {
    return
  }

  const { videoId, videoSrc } = storedVideoFromPip
  if (!videoSrc || !videoId) {
    console.error("No data found for the pip element.")
    return
  }

  const storageKey = VIDEO_STORAGE_PREFIX + videoId

  // Skip the restoration if the video is already present on the page
  if (videoId && pageVideoIds.length > 0 && pageVideoIds.includes(videoId)) {
    const videoComponentEl = document.querySelector(`#${videoId}`)
    if (videoComponentEl) {
      videoComponentEl.style.display = "none"
      videoComponentEl.dataset.pip = "true"

      const videoWrapperPipButtonEl = videoComponentEl.previousElementSibling

      if (
        videoWrapperPipButtonEl &&
        !videoWrapperPipButtonEl.classList.contains(
          `${CONSTANT_CLASS}__wrapper-exit-pip-btn`,
        )
      ) {
        const handleTogglePip = () => {
          togglePipMode(videoId, videoSrc, storageKey)
        }

        videoWrapperPipButtonEl.addEventListener("click", handleTogglePip)
      }
    }
  }

  createPipPlayer(videoId, storageKey, null, true)
}

const animateVideoOnVideoPage = (videoPage) => {
  const videoPlayerWrapperEL = document.querySelector(
    ".video-page__video-player",
  )
  const videoEl = videoPage.querySelector(".video-player.draggable")
  let pipElement = document.querySelector(
    `.${CONSTANT_CLASS}--pip-window[data-is-pip="true"]`,
  )
  const closeSlideInBtn = videoPage.querySelector(
    ".video-player__close-slidein-btn",
  )
  let keepInHero = false

  function slideInVideo() {
    // Force reflow to allow transition
    void videoEl.offsetWidth
    videoEl.classList.add("side-view")
    setTimeout(() => {
      videoEl.classList.add("transition")
    }, 100)
    setTimeout(() => {
      videoEl.classList.add("in-view")
    }, 300)
  }

  function returnVideoToComponent() {
    videoEl.classList.remove("transition")
    videoEl.classList.remove("in-view")
    videoEl.classList.remove("side-view")
  }

  // Helper to check if wrapper is in view
  function isWrapperInView() {
    if (!videoPlayerWrapperEL) return false
    const rect = videoPlayerWrapperEL.getBoundingClientRect()
    return rect.top < window.innerHeight && rect.bottom > 0
  }

  if (pipElement) {
    // If pipElement is present, do not trigger observer
    // Also disable slide-in and keep the hero video in place
    keepInHero = true
    if (videoEl) {
      videoEl.classList.remove("side-view", "transition", "in-view")
      videoEl.classList.add("keep-in-hero")
    }
    Emitter.on("close-pip-video", () => {
      if (!isWrapperInView() && !keepInHero) {
        slideInVideo()
      }
      pipElement = null
    })
  }

  if (videoEl) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (!keepInHero) slideInVideo()
        } else {
          returnVideoToComponent()
        }
      },
      { threshold: 0 },
    )
    observer.observe(videoPlayerWrapperEL)
  }

  // Handle close slide-in button: keep video in hero and disable slide-in
  if (closeSlideInBtn) {
    closeSlideInBtn.addEventListener("click", () => {
      keepInHero = true
      videoEl.classList.remove("side-view", "transition", "in-view")
      videoEl.classList.add("keep-in-hero")
    })
  }
}

// ************************************************************ //
// ********************** RUN VIDEO LOGIC ********************* //
// ************************************************************ //
export const RegisterVideoPlayer = () => {
  window.addEventListener("DOMContentLoaded", async () => {
    const videoComponents = document.querySelectorAll(".video--livestream")
    let pageVideoIds = []
    if (videoComponents.length > 0) {
      pageVideoIds = [...videoComponents].map((videoComponentEl) => {
        // Determine the base class based on the video component structure
        const baseClass = videoComponentEl.querySelector(
          ".video--livestream__container",
        )
          ? "video--livestream"
          : "video-component"

        VideoPlayer(videoComponentEl, baseClass)
        if (videoComponentEl.classList.contains("livestream")) {
          liveStreamCountdown(videoComponentEl, "video--livestream")
        }
      })
    }
    videoFromPip(pageVideoIds)
    const videoPage = document.querySelector(".video-page")

    if (videoPage) {
      VideoPlayer(videoPage, "video-page")
      animateVideoOnVideoPage(videoPage)

      if (videoPage.classList.contains("livestream")) {
        liveStreamCountdown(videoPage, "video-page")
      }
    }

    let attempts = 0

    const intervalId = setInterval(() => {
      const livestreamCards = document.querySelectorAll(
        ".node--type-live-stream.node--view-mode-card",
      )

      if (attempts > 5) {
        return clearInterval(intervalId)
      }

      if (livestreamCards.length > 0) {
        // eslint-disable-next-line no-extra-semi
        ;[...livestreamCards].forEach((card) => {
          const videoUrl = card.dataset.videoUrl

          const videoId = getYouTubeVideoId(convertToEmbedUrl(videoUrl))
          const thumbnailImg = getYouTubeThumbnail(videoId)
          const cardImgEl = card.querySelector(".card--media > img")

          if (cardImgEl) {
            cardImgEl.src = thumbnailImg
            cardImgEl.alt = "Livestream Thumbnail"
          }
        })

        return clearInterval(intervalId)
      }
      attempts += 1
    }, 1000)

    const livestreamCards = document.querySelectorAll(
      ".node--type-live-stream.node--view-mode-card",
    )

    if (livestreamCards.length > 0) {
      // eslint-disable-next-line no-extra-semi
      ;[...livestreamCards].forEach((card) => {
        const videoUrl = card.dataset.videoUrl
        const videoId = getYouTubeVideoId(convertToEmbedUrl(videoUrl))
        const thumbnailImg = getYouTubeThumbnail(videoId)
        const cardImgEl = card.querySelector(".card--media > img")

        if (cardImgEl) {
          cardImgEl.src = thumbnailImg
          cardImgEl.alt = "Livestream Thumbnail"
        }
      })
    }
  })
}
